import { useSetAtom } from "jotai";
import { useEffect } from "react";
import type { RouteWrapperProps } from "../../../components/layouts/route-wrapper/RouteWrapper";
import { RouteWrapperVariantFactory } from "../../../components/layouts/route-wrapper/variants/route-wrapper-variant-factory";
import { RouteWrapperVariant } from "../../../constants/layouts/route-wrapper-variant.enum";
import { PaywallABTestVariant } from "../../../constants/paywallABTestVariant.enum";
import { headerV2ContentStoreInitialState } from "../../../legacy/components/HeaderV2/stores/header-v2-content-store";
import {
  HeaderV2LayoutAction,
  HeaderV2LayoutStore,
  headerV2LayoutStoreInitialState,
} from "../../../legacy/components/HeaderV2/stores/header-v2-layout-store";
import { HeaderV2ContentStore } from "../../../legacy/components/HeaderV2/stores/header-v2-content-store";
import {
  HeaderV2ProgressStore,
  headerV2ProgressStoreInitialState,
} from "../../../legacy/components/HeaderV2/stores/header-v2-progress-store";
import type { IHeaderV2Store } from "../../../legacy/components/HeaderV2/stores/header-v2-store";
import { ShowBottomNavigationStore } from "../../../legacy/stores/layout/show-bottom-navigation.store";
import { ShowHeaderStore } from "../../../legacy/stores/layout/show-header.store";
import { ShowSidebarStore } from "../../../legacy/stores/layout/show-sidebar.store";
import { LayoutStylesStore } from "../../../legacy/stores/layout/layout-styles.store";
import { usePaywallABTestVariant } from "../../feature-flags/use-paywall-ab-test-variant/usePaywallABTestVariant";

const variantHandler = new RouteWrapperVariantFactory();

export function useRouteWrapperState({
  variant = RouteWrapperVariant.firstLevel,
  showLogo,
  title,
  defaultBackButtonUrl,
  defaultCloseButtonModal: closeButtonModal,
  progressBar = false,
  progressBarPercentage = 0,
}: RouteWrapperProps) {
  const setHeaderV2LayoutStore = useSetAtom(HeaderV2LayoutStore);
  const setHeaderV2ContentStore = useSetAtom(HeaderV2ContentStore);
  const setHeaderV2ProgressStore = useSetAtom(HeaderV2ProgressStore);
  const setShowBottomNavigationStore = useSetAtom(ShowBottomNavigationStore);
  const setShowHeaderStore = useSetAtom(ShowHeaderStore);
  const setShowSidebarStore = useSetAtom(ShowSidebarStore);
  const setLayoutStyles = useSetAtom(LayoutStylesStore);
  const paywallABTestVariant = usePaywallABTestVariant();

  useEffect(() => {
    const currentVariant = variantHandler.createRouteWrapperVariant(variant);
    let headerVariantSettings = currentVariant.getVariantHeaderSettings({
      title,
      showLogo,
      defaultBackButtonUrl,
      defaultCloseButtonModal: closeButtonModal,
      progressBar,
      progressBarPercentage,
    });

    if (variant === RouteWrapperVariant.upgradePage && headerVariantSettings.layoutStore?.actions) {
      const actions = [...headerVariantSettings.layoutStore.actions];
      const shouldShowClaraButton =
        paywallABTestVariant === PaywallABTestVariant.CLARA || paywallABTestVariant === null;

      if (shouldShowClaraButton) {
        if (!actions.includes(HeaderV2LayoutAction.aiAssistant)) {
          actions.unshift(HeaderV2LayoutAction.aiAssistant);
        }
        headerVariantSettings = {
          ...headerVariantSettings,
          layoutStore: {
            ...headerVariantSettings.layoutStore,
            actions,
          },
        };
      } else {
        const filteredActions = actions.filter((action) => action !== HeaderV2LayoutAction.aiAssistant);
        headerVariantSettings = {
          ...headerVariantSettings,
          layoutStore: {
            ...headerVariantSettings.layoutStore,
            actions: filteredActions,
          },
        };
      }
    }

    setShowBottomNavigationStore(currentVariant.bottomNavigationAvailable);
    setShowHeaderStore(currentVariant.headerAvailable);
    setShowSidebarStore(currentVariant.sidebarAvailable);
    setLayoutStyles(currentVariant.layoutStyles);

    updateHeaderV2LayoutStore(headerVariantSettings.layoutStore);
    updateHeaderV2ProgressStore(headerVariantSettings.progressStore);

    if (!currentVariant.titleAvailable) {
      setHeaderV2ContentStore({});
      return;
    }

    const isStaticTitle = Boolean(title);

    if (isStaticTitle) {
      setHeaderV2ContentStore({
        ...headerV2ContentStoreInitialState,
        ...headerVariantSettings.contentStore,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, showLogo, defaultBackButtonUrl, variant, paywallABTestVariant]);

  function updateHeaderV2LayoutStore(variantSettings: IHeaderV2Store["layoutStore"]) {
    if (!variantSettings) {
      setHeaderV2LayoutStore(headerV2LayoutStoreInitialState);
      return;
    }

    setHeaderV2LayoutStore({
      ...headerV2LayoutStoreInitialState,
      ...variantSettings,
    });
  }

  function updateHeaderV2ProgressStore(variantSettings?: IHeaderV2Store["progressStore"]) {
    if (!variantSettings) {
      setHeaderV2ProgressStore(headerV2ProgressStoreInitialState);
      return;
    }

    setHeaderV2ProgressStore({
      ...headerV2ProgressStoreInitialState,
      ...variantSettings,
    });
  }
}
